import { getState, selectedServices } from '../store'
import { LARGE_CAR_SERVICE, SAME_DAY_SERVICE, CONGESTION_ZONE_SERVICE } from '../store/constants/prices'
import { priceProps } from '../lib/price-lines'
import { hasRegistration } from '../lib/vehicle'
import { codeProps, codeType, refusalReason, removedTotals } from '../lib/code-props'
import { codeCreditCents } from '../lib/promo-credit'

// total_pence, size and surcharges as the booking stands (lib/price-lines.js
// priceProps), for the six booking events that carry them since 19 Oct 2026.
const priceNow = size => priceProps(getState(), { large_car: LARGE_CAR_SERVICE.id, same_day: SAME_DAY_SERVICE.id, congestion_zone: CONGESTION_ZONE_SERVICE.id }, size)

// has_registration: whether the booking carries a plate from the optional
// Registration box (ANALYTICS.md "Registration box", since 19 Oct 2026), on
// the pay events, so conversion can be split by plate users vs not. Never
// the plate itself.
// has_picked_address: whether the Car location box holds an address picked
// from its list (ANALYTICS.md "Car location", since 19 Oct 2026); StepBook
// keeps it current. Never the address.
let pickedAddress = false
const regNow = () => {
  try { return { has_registration: hasRegistration(getState().carRegistration), has_picked_address: pickedAddress } } catch (e) { return { has_registration: false, has_picked_address: pickedAddress } }
}

// The code the booking holds now (a guest's or a deferred code, else a
// signed-in percent code), for the promo events and the pay presses
// (ANALYTICS.md "Promo & referral code lifecycle", since 26 Oct 2026).
const heldCode = () => {
  try { const s = getState(); return s.promoCodeInfo || s.promoCode || null } catch (e) { return null }
}
const codeNow = () => {
  const type = codeType(heldCode())
  return type ? { has_code: true, code_type: type } : { has_code: false }
}

// Categories + actions kept as constants so call-sites stay readable. Event
// names follow the GA4 e-commerce vocabulary (add_to_cart, purchase, ...)
// because the funnel was designed on it; PostHog is where they land now.
export const categories = {
  booking: 'booking',
  landing: 'landing',
  services: 'services'
}

export const actions = {
  // booking
  setlectDate: 'Select date',
  setlectTime: 'Select time',
  setlectBaseService: 'Select base service',
  setlectExtraService: 'Select extra service',
  desetlectExtraService: 'Deselect extra service',
  bookingStep1: 'Navigate to booking step 1',
  bookingStep2: 'Navigate to booking step 2',
  bookingStep3: 'Navigate to booking step 3',
  bookingStepLogin: 'Navigate to booking login',
  bookingStep4: 'Navigate to booking step 4',
  applyPromoCode: 'Apply promo code',
  clickPlaceOrder: 'Click Place order',
  orderPlaced: 'Order placed',
  // landing
  isAvailableInMyArea: 'Is GoWashMyCar available in my area?',
  contactWhatsapp: 'Open WhatsApp from FAB',
  // services
  bookNow: 'Book now'
}

// Every event goes to PostHog (loaded by the layout when POSTHOG_KEY is set)
// with the names in ANALYTICS.md, so every funnel drop-off links to a session
// replay. GA4 only receives page views (index.js) for acquisition reporting.
// The tracker may be absent (ad-blocker, dev env); then this is a no-op.
// options: posthog-js capture options, only for an event sent as the page
// goes away (BEACON below).
const fire = (name, params, options) => {
  if (typeof window === 'undefined') return
  if (window.posthog && typeof window.posthog.capture === 'function') {
    try {
      if (options) window.posthog.capture(name, params, options)
      else window.posthog.capture(name, params)
    } catch (e) { /* never break the booking flow for analytics */ }
  }
}

// Sent now, by navigator.sendBeacon, not queued: the page may be unloading.
const BEACON = { transport: 'sendBeacon', send_instantly: true }

// Booking step names: call-sites pass the route param (see booking-page.js),
// which is 'extra' (singular) for the add-ons step. The canonical GA4 step
// vocabulary — what the funnel report + ANALYTICS.md key on — is 'extras'
// (plural), so normalize at the analytics boundary. This is why the funnel
// showed extras=0: the event fired as 'extra' and never matched. NOTE:
// BOOKING_STEP_INDEX stays keyed by the route param 'extra' on purpose.
const canonicalStepName = (step) => (step === 'extra' ? 'extras' : step)

// Map our local product shape to GA4's `items` array format.
const itemFromProduct = ({ name, id, gwmcPrice, group }) => ({
  ...(id && { item_id: id.toString() }),
  ...(name && { item_name: name }),
  ...(group && { item_category: group }),
  ...(gwmcPrice && { price: gwmcPrice }),
  quantity: 1
})

// True when PostHog already holds an identified person other than `id`.
// Anonymous visitors carry a UUID; identified ones carry users.id, so a
// numeric distinct_id that is not ours means someone else signed in here.
const identifiedAsSomeoneElse = (id) => {
  try {
    const ph = window.posthog
    if (!ph || typeof ph.get_distinct_id !== 'function') return false
    const current = ph.get_distinct_id()
    if (!current || String(current) === id) return false
    const state = typeof ph.get_property === 'function' ? ph.get_property('$user_state') : undefined
    return state === 'identified' || /^\d+$/.test(String(current))
  } catch (e) {
    return false
  }
}

export class AnalyticsService {
  static trackEvent = (category, action, label, value, extra) => {
    fire(action.toString(), {
      ...extra,
      event_category: category.toString(),
      ...(label && { event_label: label.toString() }),
      ...(value !== undefined && value !== null && { value: Number(value) || 0 }),
      non_interaction: category !== categories.booking
    })
  }

  static trackImpressions = step => {
    const state = getState()
    const allProducts = state.prices.ids.map(serviceId => state.prices.byId[serviceId])
    const baseProducts = allProducts.filter(product => product.category === 'base')
    const extraProducts = allProducts.filter(product => ['popular', 'extras', 'detailing'].includes(product.category))
    const surchargeProducts = allProducts.filter(product => product.category === 'surcharge')
    const stepProducts = { 1: surchargeProducts, 2: baseProducts, 3: extraProducts }[step] || []

    fire('view_item_list', {
      currency: 'GBP',
      item_list_id: `step_${step}`,
      items: stepProducts.map(itemFromProduct)
    })
  }

  static trackAddToCart = (product, extra) => {
    fire('add_to_cart', {
      ...extra,
      currency: 'GBP',
      items: [itemFromProduct(product)]
    })
  }

  static trackRemoveFromCart = (product, extra) => {
    fire('remove_from_cart', {
      ...extra,
      currency: 'GBP',
      items: [itemFromProduct(product)]
    })
  }

  static trackCheckoutStep = step => {
    const state = getState()
    // A selected id whose price has not loaded yet is skipped, not thrown on.
    const services = selectedServices(state).filter(service => service && !(step === 1 && service.group === 'base'))
    const items = services.map(itemFromProduct)

    // GA4 split the old monolithic 'checkout' progression into discrete
    // events. Step 1 in our flow is the start of checkout; the final
    // payment step maps to add_payment_info. Other steps surface as a
    // generic `checkout_step` so they're still visible in reports.
    const eventName = step === 1 ? 'begin_checkout' : (step >= 5 ? 'add_payment_info' : 'checkout_step')

    fire(eventName, {
      currency: 'GBP',
      checkout_step: step,
      ...(items.length > 0 && { items })
    })
  }

  static trackPurchase = (data, products, coupon) => {
    const { id = 0, net_gwmc_fee = 0 } = data || {}
    const revenue = Number(net_gwmc_fee) || 0
    const tax = Math.round(16.6667 * revenue) / 100

    fire('purchase', {
      transaction_id: id.toString(),
      value: revenue,
      tax,
      currency: 'GBP',
      ...(coupon && { coupon }),
      items: products.map(itemFromProduct)
    })
  }

  // ============================================================
  // GA4 funnel + diagnostic events for the booking wizard.
  //
  // These are additive to the legacy `trackEvent` / `trackCheckoutStep`
  // calls above (which keep populating existing reports). Names are
  // snake_case + semantically scoped so GA4's Funnel Exploration and
  // Free-Form Explorations can pivot on them directly. Mobile/desktop
  // split is automatic in GA4 via `device_category`; no event needed.
  // ============================================================

  // step_name: 'service' | 'details' | 'extras' | 'checkout' | 'finish'
  static trackBookingStepViewed = (stepName, stepIndex) => {
    fire('booking_step_viewed', { step_name: canonicalStepName(stepName), step_index: stepIndex, ...priceNow() })
  }

  // extra: on Book & pay's press that goes through to payment, the note
  // (lib/booking-progress.js noteProps: notes_text, notes_length,
  // house_number_filled; since 17 Oct 2026).
  static trackBookingStepCompleted = (stepName, stepIndex, extra = {}) => {
    fire('booking_step_completed', { step_name: canonicalStepName(stepName), step_index: stepIndex, ...extra, ...regNow() })
  }

  // source: 'homepage_postcode' | 'homepage_service_tile' | 'services_page_tile'
  // NB: emit as `entry_source`, NOT `source` — `source`/`medium`/`campaign`
  // are GA4 reserved params that override traffic-source attribution (sending
  // them made booking entries show up as fake acquisition sources).
  static trackBookingEntry = (source, extra = {}) => {
    fire('booking_entry', { entry_source: source, ...extra })
  }

  // postcode_result is retired (18 Oct 2026): the postcode result modal that
  // sent it is gone and nothing called it. Historic events stay in PostHog.

  // address_resolved is retired (18 Oct 2026): nothing had called it since
  // the typed address (4 Oct), and the wallet sheet's address now has its
  // own events, wallet_address_changed / wallet_address_rejected (below).
  // Historic events in PostHog still carry it.

  // method: the specific payment rail, not just card-vs-wallet, so we can read
  // a per-method funnel (e.g. Apple Pay vs Google Pay failure rates):
  //   'card'         - hosted Stripe Checkout card flow (redirect)
  //   'apple_pay' | 'google_pay' | 'link' - the named wallets
  //   'browser_card' - a saved card surfaced via the wallet sheet
  static trackPaymentMethodSelected = (method) => {
    fire('payment_method_selected', { method: method || 'unknown' })
  }

  // wallets: which rails Stripe's canMakePayment() said were usable
  // (e.g. ['apple_pay'] / ['google_pay'] / ['link']). Lets us measure uptake
  // per wallet — "of users shown Apple Pay, how many used it".
  // shown: the one wallet button the embedded checkout shows (by platform).
  static trackWalletAvailable = (wallets, shown) => {
    fire('wallet_available', { wallets: Array.isArray(wallets) ? wallets.join(',') : (wallets || ''), ...(shown && { shown }) })
  }

  // method on the wallet events = the specific wallet (apple_pay/google_pay/
  // link/browser_card), threaded from Stripe's paymentRequest walletName.
  static trackWalletPaymentStarted = (method) => {
    fire('wallet_payment_started', { method: method || 'wallet', ...regNow(), ...codeNow() })
  }

  // The Apple Pay / Google Pay / Link sheet opened (the Express Checkout
  // Element's click, resolved) and was dismissed (its cancel). props from
  // lib/wallet-sheet.js: method; on cancel also seconds_open and
  // after_confirm (the payer had approved and the payment then failed).
  // Device and browser come with every event ($device_type, $browser, $os).
  static trackWalletSheetOpened = (props) => {
    fire('wallet_sheet_opened', props || { method: 'wallet' })
  }

  static trackWalletSheetCancelled = (props) => {
    fire('wallet_sheet_cancelled', props || { method: 'wallet' })
  }

  // The wallet sheet's address (18 Oct 2026, lib/wallet-address.js): chosen
  // or changed in the sheet and checked by the server. NEVER the address or
  // the phone: the outward code ("SW6") is all of it an event carries.
  // changed: method, postcode_outward, outside_usual_area,
  //   may_be_in_congestion_zone, price_changed, checked (false: the check
  //   could not be asked and the page's price stood).
  // rejected: method, reason ('not_gb' | 'no_postcode' | 'booking' |
  //   'outside_area'), postcode_outward when there was one.
  static trackWalletAddressChanged = (props) => {
    fire('wallet_address_changed', props || {})
  }

  static trackWalletAddressRejected = (reason, props = {}) => {
    fire('wallet_address_rejected', { reason: reason || 'unknown', ...props })
  }

  static trackWalletPaymentCompleted = (method) => {
    fire('wallet_payment_completed', { method: method || 'wallet' })
  }

  // details: what Stripe said, from lib/stripe-load.js stripeErrorDetails
  // (error_code, decline_code, error_type, error_message; no PII), or the
  // same keys from our own API's answer for setup_intent_failed. Without it
  // the 5 Oct Apple Pay failures were three events and no reason.
  static trackWalletPaymentFailed = (reason, method, details) => {
    fire('wallet_payment_failed', { reason: reason || 'unknown', method: method || 'wallet', ...(details || {}) })
  }

  // Stripe.js did not load. attempt 1 (will_retry: true) is followed by one
  // retry; attempt 2 is the give-up, after which the pay panels offer
  // Stripe's hosted card page. attempt 0: Stripe.js loaded but would not start.
  static trackStripeJsLoadFailed = (message, attempt, willRetry) => {
    let online
    try { online = typeof navigator !== 'undefined' ? navigator.onLine : undefined } catch (e) { /* ignore */ }
    fire('stripe_js_load_failed', {
      attempt,
      will_retry: !!willRetry,
      ...(message && { error_message: String(message).slice(0, 200) }),
      ...(online !== undefined && { online })
    })
  }

  // The "Pay by card" offered when Stripe.js failed to load was tapped.
  static trackStripeFallbackTapped = () => {
    fire('stripe_fallback_card_tapped', {})
  }

  // attempt: the how-many-th card try in this tab (1 the first), counted in
  // the checkout attempt (lib/payer-email.js), a reload included.
  static trackCardPaymentStarted = attempt => {
    fire('card_payment_started', { ...(attempt ? { attempt } : {}), ...regNow(), ...codeNow() })
  }

  // A 3DS check started during an embedded confirm (lib/confirm-watch.js saw
  // the SetupIntent at requires_action). kind: card | wallet.
  static trackThreeDsStarted = kind => {
    fire('threeds_started', { kind })
  }

  // What came of it: status succeeded | failed | unknown, Stripe's code, and
  // left_page, confirm_seconds, settled_by (confirm | check).
  static trackThreeDsResult = (status, code, extra) => {
    fire('threeds_result', { status, ...(code && { code }), ...(extra || {}) })
  }

  // The card path's email was tidied before use (lib/payer-email.js
  // cleanEmailWithFixes). Which fixes only, never the address.
  static trackEmailCleaned = fixes => {
    fire('email_cleaned', { fixes })
  }

  // AN EMAIL WE CANNOT USE, DROPPED AND THE BOOKING TAKEN (1 Nov 2026). The
  // press used to be refused here (`field_blocked` email, the fourth and last
  // refusal of ours); now the tidied address is simply not stored when it is
  // no address, Order#missing_details reports :email and Aider asks. This is
  // the count of bookings that costs: reason ('missing' or 'not_an_email')
  // and the rule it failed, never the address itself.
  static trackEmailUnusable = (reason, rule = null) => {
    fire('email_unusable', { step_name: 'book', reason, ...(rule && { rule }) })
  }

  // Embedded checkout: "Pay by card" tapped to open the card section under
  // the wallet row (not fired when it opens by itself for want of a wallet).
  static trackCardFormOpened = () => {
    fire('card_form_opened', {})
  }

  // Embedded checkout: the unpaid line above the wallet button came on
  // screen (lib/unpaid-status.js), once per reason per page view.
  // reason: missing | ready | returned; missing: the analytics field names
  // still empty; wallet: apple_pay | google_pay | link | card; missing_count:
  // how many; next_field: the one the line links to (null when none).
  static trackUnpaidStatusShown = (reason, missing, wallet, missingCount, nextField) => {
    const list = missing || []
    fire('unpaid_status_shown', { reason, missing: list, wallet: wallet || 'card', missing_count: missingCount ?? list.length, next_field: nextField || list[0] || null, ...priceNow() })
  }

  // Book & pay on a phone: the sticky bar's Book button, which scrolls down
  // to the postcode, house number, mobile and pay buttons.
  // target: where the bar's button took them, the first thing still to do
  // ('car_size' | 'date' | 'details' | 'pay'; since 9 Oct 2026).
  static trackMobileBarBookTapped = target => {
    fire('mobile_bar_book_tapped', target ? { target } : {})
  }

  // Book & pay, 9 Oct 2026 (ANALYTICS.md "Asked at the pay buttons"). No
  // personal data in any of these: field names, sizes and counts only,
  // except the customer's note on pay_panel_shown (since 17 Oct 2026,
  // docs/DECISIONS.md).
  //
  // size: 'small' | 'large'; source: 'form' (the size buttons), 'pay_panel'
  // (asked at the pay buttons) or 'lookup' (Form v3: the plate's car set
  // it). size_source (since 20 Oct 2026): 'lookup', 'manual' (picked, no
  // size from the plate) or 'changed' (picked after "Not right? Change
  // size"). Every pick, not only the first, so large-versus-small drop-off
  // can be read.
  static trackCarSizeSelected = (size, source, sizeSource) => {
    fire('car_size_selected', { source, size_source: sizeSource || 'manual', ...priceNow(size) })
  }

  // Form v3: the plate's car is on the Large list and the form set Large
  // itself, total_pence with the surcharge in. ANALYTICS.md "Size from the
  // plate" has the query for a leave within 30 s of it.
  static trackLargeAutoApplied = () => fire('large_auto_applied', priceNow('large'))

  // A pay press found something missing and the panel at the buttons asked
  // for it. missing: analytics field names ('car_size', 'date', 'postcode',
  // 'house_number', 'mobile'); kind: 'card' | 'wallet'. Once per page view
  // until it is answered. extra: noteProps (notes_text, notes_length,
  // house_number_filled; since 17 Oct 2026).
  static trackPayPanelShown = (missing, kind, extra = {}) => {
    fire('pay_panel_shown', { missing, pay_kind: kind || null, ...extra, ...regNow(), ...priceNow() })
  }

  // The next press found nothing missing: what had been asked, how long it
  // took, and which button went on to pay (card_payment_started or the
  // wallet_* events follow as before).
  static trackPayPanelCompleted = (missing, seconds, kind) => {
    fire('pay_panel_completed', { missing, seconds, pay_kind: kind || null, ...regNow() })
  }

  // A signed-in customer's saved mobile was not a UK mobile ("12345678"),
  // so the box started empty instead of showing it.
  static trackMobilePrefillSkipped = () => {
    fire('mobile_prefill_skipped', {})
  }

  // The Book & pay postcode box, on blur, once per distinct postcode.
  // result: 'ok' (postcodes.io knows it) | 'not_found_soft' (it does not:
  // a soft note, never a refusal) | 'out_of_area_soft' (outside London and
  // the South East: a soft note, books) | 'bad_format' | 'unknown'
  // (postcodes.io did not answer). outward: the outward code only
  // ("SW6"), never the whole postcode.
  static trackPostcodeChecked = (result, outward) => {
    fire('postcode_checked', { result, outward: outward || null })
  }

  // "Do we cover you?" on a wash page or /services (lib/coverage-check).
  // result: 'usual' | 'outside' | 'invalid'; wash: the wash page's slug
  // ('full-valet'), null on /services; outward: the outward code only
  // ("SW6"), never the whole postcode.
  static trackCoverageChecked = ({ result, wash, outward }) => {
    fire('coverage_checked', { result, wash: wash || null, outward: outward || null })
  }

  // "Not this much? Mini Valet PLUS from £95" under a dearer wash's price.
  // from / to: wash page slugs. By beacon: the link leaves the page.
  static trackWashStepDownClicked = ({ from, to }) => {
    fire('wash_step_down_clicked', { from, to }, BEACON)
  }

  // A day chip on Book & pay (components/day-chips), or a day from its
  // "More dates" calendar, or one picked in the panel at the pay buttons.
  // props: { days_ahead, weekday, is_today, source: chip | more_dates |
  // pay_panel }. Never the date itself.
  static trackBookingDateChipSelected = props => {
    fire('booking_date_chip_selected', props)
  }

  static trackBookingMoreDatesOpened = () => {
    fire('booking_more_dates_opened', {})
  }

  // Back on /booking/book from Stripe's hosted page without paying: its
  // cancel link (cancelled: true) or the browser's Back (false).
  static trackStripeCheckoutReturned = cancelled => {
    fire('stripe_checkout_returned', { cancelled: !!cancelled })
  }

  // Progress through Book & pay, top to bottom (ANALYTICS.md, "Progress
  // through Book & pay"; lib/booking-progress.js). section: 'wash' |
  // 'car_size' | 'date' | 'time_slot' | 'notes' | 'extras' | 'address' |
  // 'contact' | 'pay_buttons'; section_index is its 1-based screen position.
  static trackBookingSectionViewed = (section, sectionIndex, formVariant) => {
    fire('booking_section_viewed', { section, section_index: sectionIndex, step_name: 'book', form_variant: formVariant, ...priceNow() })
  }

  // Once per page view, when the visitor leaves the screen without paying.
  // The Car location box (ANALYTICS.md "Car location"): car_location_<name>
  // for focused, suggestions_shown, picked, manual and not_covered. The lazy
  // address-pick script sends through it too (window.gwmcTrackCarLocation).
  static trackCarLocation = (name, props) => fire(`car_location_${name}`, props || {})

  static setPickedAddress = on => { pickedAddress = !!on }

  static trackBookingFormExit = (props) => {
    fire('booking_form_exit', { step_name: 'book', ...props, ...regNow(), ...priceNow() }, BEACON)
  }

  // The Registration box (components/reg-lookup, lib/vehicle.js regTracker;
  // ANALYTICS.md "Registration box", since 19 Oct 2026; required since 20
  // Oct). No plate, make or model in any of them. fire('registration_<name>'):
  //   entered: once per distinct plate typed and looked up. props: result
  //     (found | not_found | error), suggested_size (large | null),
  //     picked_size (small | large | null, at that moment), plate_index (1
  //     the first plate this page view), form_variant, path (wallet | card).
  //   size_hint_shown / size_hint_followed: the "This looks like a Large
  //     car." line appeared (Small to medium picked, the car on the Large
  //     list), and Large was then picked. Once each per plate per page view.
  //     props: form_variant, path.
  //   invalid_format: a typed plate in no UK format, when the customer left
  //     the box (lib/vehicle.js regProblem). props: reason (too_long |
  //     no_digit | no_letter | pattern), form_variant, path.
  static trackRegistration = (name, props) => fire(`registration_${name}`, props || {})

  // method: 'google' | 'email' | 'guest'
  static trackSigninMethodSelected = (method) => {
    fire('signin_method_selected', { method })
  }

  // valueInPounds is the surcharge added (numeric, e.g. 45)
  // Same day, when the form cannot take it (17 Oct 2026, lib/same-day-offer.js).
  // reason: 'too_late' (today asked for after the cutoff, by the form or
  // refused by the server at pay), 'moved' (another day the form moved) or
  // 'full' (reserved for a slot capacity check; not sent yet).
  static trackSameDayUnavailable = reason => {
    fire('same_day_unavailable', { reason: reason || 'moved' })
  }

  // The "WhatsApp us" button of "Need it today?" was tapped. reason: why
  // the offer showed ('today_selected', 'moved' or 'too_late').
  static trackSameDayWhatsappTapped = reason => {
    fire('same_day_whatsapp_tapped', { reason: reason || 'today_selected' })
  }

  static trackSameDaySurchargeApplied = (valueInPounds) => {
    fire('same_day_surcharge_applied', {
      value: Number(valueInPounds) || 0,
      currency: 'GBP'
    })
  }

  // ---- Identity (ANALYTICS.md §9) ---------------------------------------
  // The identity axis in BOTH tools is users.id — stable, non-PII, and the
  // key you'd look someone up by in the DB/admin. Never email or phone.
  // identify() merges the anonymous browsing that preceded it into that
  // person, so first-visit → sign-up → paid stays one journey. Called from
  // the SET_USER side effect (startup with a session, sign-in, sign-up,
  // guest sign-up, social logins); reset() from SIGN_OUT so a shared device
  // never glues two people together.
  //
  // reset() runs ONLY on real sign-out, plus here when this browser is
  // already identified as a *different* user. Never reset an anonymous
  // visitor before identifying them: that throws away the distinct_id the
  // funnel and any flag exposure were recorded under, splitting one booking
  // into two people (the reset() in apiGuestSignUp that blinded the
  // booking-flow experiment, docs/DECISIONS.md 2026-10-04).
  static identify = (user) => {
    if (!user || !user.id || typeof window === 'undefined') return
    const id = String(user.id)
    if (identifiedAsSomeoneElse(id)) AnalyticsService.reset()
    let isGuest = false
    try { isGuest = window.localStorage.getItem('isGuest') === 'true' } catch (e) { /* storage blocked */ }
    // created_at comes from the API as epoch seconds.
    const createdAt = typeof user.created_at === 'number' ? new Date(user.created_at * 1000).toISOString() : undefined
    const traits = { is_guest: isGuest, account_created_at: createdAt }
    // Us, not a customer (app/models/internal_traffic.rb): the person gets
    // is_internal and every event from this browser `internal: true`, so
    // analysis can leave them out (ANALYTICS.md §1). Guests carry no flag.
    const internal = typeof user.internal === 'boolean' ? user.internal : null
    if (internal !== null) traits.is_internal = internal
    const ph = window.posthog
    if (ph && typeof ph.identify === 'function') {
      try {
        ph.identify(id, traits)
        if (internal) {
          ph.register({ internal: true })
          ph.setPersonProperties({ is_internal: true })
        } else if (internal === false) ph.unregister('internal')
      } catch (e) { /* analytics never breaks the app */ }
    }
  }

  static reset = () => {
    if (typeof window === 'undefined') return
    if (window.posthog && typeof window.posthog.reset === 'function') {
      try { window.posthog.reset() } catch (e) { /* ignore */ }
    }
  }

  // info: the server's answer for the code ({ code, code_type, code_id,
  // value, min_basket }); via: typed | link (a ?promo_code= URL) | sign_in
  // (a held code applied to the account). Never a referral or reward code
  // itself (lib/code-props.js).
  static trackPromoCodeApplied = (info, via) => {
    fire('promo_code_applied', { ...codeProps(info), via: via || 'typed' })
  }

  // data: the server's refusal ({ refusal, code_type, code_id }); stage:
  // typed | link | sign_in | pay_press. At the pay press the code comes off,
  // and the event says the total the page moved from and to.
  static trackPromoCodeRejected = (data, stage, code) => {
    const held = stage === 'pay_press' ? heldCode() : null
    const props = { ...codeProps({ ...held, ...data, code: code || held?.code }), reason: refusalReason(data), stage: stage || 'typed' }
    if (held) {
      const basket = priceNow().total_pence
      Object.assign(props, removedTotals(held, basket, codeCreditCents(held, basket || 0)))
    }
    fire('promo_code_rejected', props)
  }

  // errorType: 'network' | 'stripe' | 'session_create' | 'finalize' | 'wallet' | etc.
  // details: lib/stripe-load.js stripeErrorDetails; its error_type (Stripe's
  // own, e.g. card_error) goes as stripe_error_type so ours stays put.
  static trackStripeError = (errorType, message, details) => {
    const { error_type: stripeType, ...rest } = details || {}
    fire('stripe_error', {
      error_type: errorType || 'unknown',
      ...(message && { error_message: String(message).slice(0, 200) }),
      ...rest,
      ...(stripeType && { stripe_error_type: stripeType })
    })
  }

  // Fired when the user tries to advance but validation blocks them.
  // step_name = current step; reason = short tag (e.g. 'missing_address').
  static trackValidationBlocked = (stepName, reason) => {
    fire('validation_blocked', { step_name: canonicalStepName(stepName), reason: reason || 'unknown' })
  }

  // Like validation_blocked, but names the SPECIFIC field that's missing/invalid
  // (e.g. 'carRegistration', 'carSize'). Split by GA4's built-in device_category
  // to see which field stalls mobile vs desktop on the multi-field Details step.
  // `reason` is optional: the Book & pay location fields send why (missing /
  // not_a_postcode / postcode_not_found); older call-sites send none.
  // extra: more about why, e.g. the email's `rule` (lib/payer-email.js).
  static trackFieldBlocked = (stepName, field, reason, extra) => {
    fire('field_blocked', { step_name: stepName, field: field || 'unknown', ...(reason && { reason }), ...(extra || {}) })
  }

  // First meaningful interaction with a form field (first focus / first change),
  // deduped once per field per step by the caller. Reuses the same `field` +
  // `step_name` dims as field_blocked so the two compose into a per-field
  // engage→block→complete funnel: how many users TOUCH each field vs how many
  // stall on it. This is what lets us pinpoint the exact field where users go
  // silent and abandon the multi-field Details step (no Next click, no error).
  static trackFieldEngaged = (stepName, field) => {
    fire('field_engaged', { step_name: stepName, field: field || 'unknown' })
  }

  // Booking date field (ANALYTICS.md §2, "Booking date field"). Properties
  // are whole-day offsets from lib/date-input.js, never the date itself.
  // Engagement is the existing field_engaged(field='date').
  //  booking_date_set       - a valid date was applied: { days_ahead,
  //                           input_method: 'typed' | 'picker', same_day }
  //  booking_date_corrected - a past / after-cutoff day was moved to the
  //                           earliest day: { reason: 'past' | 'after_cutoff',
  //                           from_days_ahead, input_method }
  //  booking_date_invalid   - a submit / pay press was refused because of the
  //                           date: { step_name, reason: 'required' | 'invalid' }
  static trackBookingDateSet = (props) => {
    fire('booking_date_set', props)
  }

  static trackBookingDateCorrected = (props) => {
    fire('booking_date_corrected', props)
  }

  static trackBookingDateInvalid = (stepName, reason) => {
    fire('booking_date_invalid', { step_name: canonicalStepName(stepName), reason: reason === 'required' ? 'required' : 'invalid' })
  }

  // Fired at signup time with the final state of the WhatsApp marketing
  // opt-in checkbox + the payment method that triggered the signup. Lets
  // us measure opt-in rate per payment method (e.g. is Apple Pay
  // converting fewer users into WA marketing than typed-guest?).
  // method: 'card' | 'wallet' | 'google' | 'email' | 'signed_in'
  static trackWhatsappMarketingOptin = (optedIn, method) => {
    fire('whatsapp_marketing_optin', {
      opted_in: !!optedIn,
      method: method || 'unknown'
    })
  }

  // Fired on every checkbox interaction - captures intent before payment.
  // Pair with whatsapp_marketing_optin (at signup) to see how often
  // customers toggle multiple times / change their mind, vs the final
  // value they actually submit with.
  static trackWhatsappOptinToggled = (checked) => {
    fire('whatsapp_optin_toggled', { checked: !!checked })
  }

  // Desktop "Chat on WhatsApp": the number form opened, and a number was
  // submitted. The site loses ~75% of visitors before any engagement, so
  // these two are the capture rate of the one contact route that works on a
  // laptop. `sent` is false when we took the number but the WhatsApp did
  // not go.
  static trackWhatsappChatOpened = () => {
    fire('whatsapp_chat_opened', {})
  }

  // The dialog's "Open WhatsApp" button: wa.me, which opens WhatsApp Desktop
  // when installed (13 Oct 2026).
  static trackWhatsappChatOpenApp = () => {
    fire('whatsapp_chat_open_app', {})
  }

  static trackWhatsappChatRequested = (sent) => {
    fire('whatsapp_chat_requested', { sent: !!sent })
  }

  // The floating WhatsApp button, bottom right (13 Oct 2026). `path` is the
  // page it was tapped on. On a desktop whatsapp_chat_opened follows.
  static trackWhatsappFabTapped = (path) => {
    fire('whatsapp_fab_tapped', { path })
  }

  // The phone's chat chooser (25 Oct 2026; a desktop one from 13 Oct,
  // docs/CHATWOOT.md Website chat): "Chat here" (Chatwoot widget) or
  // "WhatsApp". No name or number ever goes in these; whatsapp_handoff_sent
  // is the server's.
  static trackChatChooserOpened = () => {
    fire('chat_chooser_opened', {})
  }

  // The widget actually opened (`ok`), or failed to load and WhatsApp was offered.
  static trackChatWidgetOpened = (ok) => {
    fire('chat_widget_opened', { ok: !!ok })
  }

  // The website chat Aider answers (CHAT_ENTRY=webchat, 25 Oct 2026). The
  // visitor's first message in a chat (Chatwoot's on-start-conversation),
  // and "Continue on WhatsApp" under the open chat. No text, no reference.
  static trackChatMessageSent = () => {
    fire('chat_message_sent', {})
  }

  static trackChatHandoffWhatsapp = (path) => {
    fire('chat_handoff_whatsapp', { path })
  }

  // Unfinished bookings (lib/booking-capture.js, BookingDraft; ANALYTICS.md
  // "Unfinished bookings"). booking_draft_saved: the booking went to the
  // server with a contact, on the first save of the session (`first`) and
  // whenever a contact kind is added. Never the email or mobile itself.
  static trackBookingDraftSaved = ({ first, hasEmail, hasMobile }) => {
    fire('booking_draft_saved', { first: !!first, has_email: !!hasEmail, has_mobile: !!hasMobile })
  }

  // The reminder's link opened Book & pay (/booking?resume=…): `restored`
  // is false when the link was bad, expired or the booking had nothing to
  // put back.
  static trackBookingResumed = ({ restored, status }) => {
    fire('booking_resumed', { restored: !!restored, ...(status ? { draft_status: status } : {}) })
  }

  static posthogDistinctId = () => {
    try {
      const ph = typeof window !== 'undefined' && window.posthog
      return ph && typeof ph.get_distinct_id === 'function' ? ph.get_distinct_id() : null
    } catch (e) {
      return null
    }
  }
}

// Step → ordered index for the new flow. Kept here so call-sites stay
// consistent (no magic numbers scattered across components).
export const BOOKING_STEP_INDEX = {
  service: 1,
  details: 2,
  book: 2, // book-and-pay arm: details + extras + checkout in one
  // Route param + STEPS.EXTRA are 'extra' (singular) — keying this 'extras'
  // made BOOKING_STEP_INDEX['extra'] undefined, so the extras step never
  // logged a booking_step_viewed (funnel showed extras=0). Match the route.
  extra: 3,
  checkout: 4,
  success: 5,
  finish: 5
}

// The lazy address-pick script (client/packs/address-pick.js) reports the
// Car location box through the same service.
if (typeof window !== 'undefined') window.gwmcTrackCarLocation = AnalyticsService.trackCarLocation
